import AddIcon from "@mui/icons-material/Add";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import MonitorHeartOutlined from "@mui/icons-material/MonitorHeartOutlined";
import { Box, InputAdornment, Typography } from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { DaysInWeek, DaysInWeekSelector } from "../../../../components/RecurringShift/util/DaysInWeekSelector";
import { DateInput } from "../../../../components/UIToolkit/Inputs/DateInput";
import { SystemAutoComplete } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { ReadOnlyMetadataField } from "../../../../components/UIToolkit/molecules/ReadOnlyMetadataField";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { Department } from "../../../../contexts/AuthProvider";
import { useRoleColors } from "../../../../hooks/useRoleColors";
import { INK } from "../../../../theme/tokens";
import { floatToTime, validateTimeInput } from "../../../../utils/floatHours";
import { DepartmentType, type HppdTarget, hppdTargetAverage, NurseFullRole, NurseRole, RemoveUnionStaffWarning, StatsMode } from "../../../../utils/models";
import { getWeekdayName, nurseRoleDisplayName } from "../../../../utils/utilFunctions";
import { DEFAULT_NIGHT_PAY_VALUE, DEFAULT_WEEKEND_PAY_VALUE, PARTIAL_SHIFT_BLOCK_HOURS, TIMEZONES } from "../utils";
import { SIDE_LAID_GROUND_SX, SUBGROUP_BORDERED_SX } from "./ratesConsts";
import type { GeneralFormData } from "./types";

type TargetHppdRow = { id: string; days: DaysInWeek[]; target: number };

const dayToHppdKey = (d: DaysInWeek): keyof HppdTarget => d.toLowerCase() as keyof HppdTarget;

const hppdToRows = (target: HppdTarget): TargetHppdRow[] => {
	const groupsByValue = new Map<number, DaysInWeek[]>();
	for (const fullDay of Object.values(DaysInWeek)) {
		const value = target[dayToHppdKey(fullDay)];
		const existing = groupsByValue.get(value) ?? [];
		existing.push(fullDay);
		groupsByValue.set(value, existing);
	}
	return Array.from(groupsByValue.entries()).map(([value, days]) => ({
		id: uuidv4(),
		days,
		target: value,
	}));
};

// Days not in any row default to 0 — merge-on-delete keeps every day covered.
const rowsToHppd = (rows: TargetHppdRow[]): HppdTarget => {
	const result: HppdTarget = { monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0 };
	for (const row of rows) {
		for (const day of row.days) {
			result[dayToHppdKey(day)] = row.target;
		}
	}
	return result;
};

const SHIFT_DELTA_THRESHOLD_MAX_HOURS = 6;
const SHIFT_DELTA_THRESHOLD_STEP_HOURS = 0.5;
const DEFAULT_NURSE_INACTIVITY_ALERT_DAYS = 70;

// Mirror the backend department_id generator (`database/departments.py` create_new_dept):
// use Unicode `\p{L}\p{N}_` (not ASCII `\W`) and always join with `__`, matching Python's `\w`.
const sanitizeIdPart = (value: string) => value.replace(/[^\p{L}\p{N}_]/gu, "_");
const buildDepartmentIdPreview = (displayName: string, hospital: string) => `${sanitizeIdPart(hospital)}__${sanitizeIdPart(displayName)}`;

// Responsive grid: a field column never gets narrower than this before wrapping.
const FIELD_MIN_COLUMN_WIDTH = 240;
// Width cap for each side-laid section's controls, so the right edge aligns across sections.
const SECTION_CONTENT_MAX_WIDTH = 900;

export const GeneralTabContent: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isNewDept: boolean;
	/**
	 * Timezone edits are frozen once the department has shifts — changing it would re-label every
	 * existing timestamp. Passed in rather than derived here so the decision lives in exactly one
	 * place (`useDeptConfig`), which is also where the fail-safe default is documented. The server
	 * enforces the same rule on save.
	 */
	isTimezoneLocked: boolean;
	/**
	 * True when the lock above is only a precaution — the has-shifts probe failed, so we don't
	 * actually know whether the department has shifts. Renders the explanatory banner, because a
	 * silently disabled field is indistinguishable from a real "this can never change".
	 */
	isTimezoneLockUnknown: boolean;
}> = ({ formData, setFormData, isNewDept, isTimezoneLocked, isTimezoneLockUnknown }) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const departmentTypeId = useId();
	const masterScheduleWeeksId = useId();

	const shiftDeltaThresholdOptions = useMemo(
		() =>
			Array.from({ length: SHIFT_DELTA_THRESHOLD_MAX_HOURS / SHIFT_DELTA_THRESHOLD_STEP_HOURS }, (_, i) => (i + 1) * SHIFT_DELTA_THRESHOLD_STEP_HOURS).map(
				(n) => ({ value: n, label: t("department.config.general.shiftDeltaThreshold.option", { count: n }) }),
			),
		[t],
	);

	const [targetHppdRows, setTargetHppdRows] = useState<TargetHppdRow[]>(() => hppdToRows(formData.targetHPPDPerDay));

	const updateTargetHppdRows = (newRows: TargetHppdRow[]) => {
		setTargetHppdRows(newRows);
		const perDay = rowsToHppd(newRows);
		setFormData((prev) => ({ ...prev, targetHPPDPerDay: perDay, targetHPPD: hppdTargetAverage(perDay) }));
	};
	const handleAddTargetHppdRow = () => updateTargetHppdRows([...targetHppdRows, { id: uuidv4(), days: [], target: 0 }]);
	// Fold the deleted row's days into the first remaining row — orphans default to 0 and silently lower the target.
	const handleRemoveTargetHppdRow = (id: string) => {
		const rowToRemove = targetHppdRows.find((r) => r.id === id);
		const remaining = targetHppdRows.filter((r) => r.id !== id);
		if (!rowToRemove || remaining.length === 0) return;
		const merged = remaining.map((r, i) => (i === 0 ? { ...r, days: [...r.days, ...rowToRemove.days] } : r));
		updateTargetHppdRows(merged);
	};
	const handleUpdateTargetHppdRow = (id: string, patch: Partial<Omit<TargetHppdRow, "id">>) =>
		updateTargetHppdRows(targetHppdRows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

	const disabledDaysPerTargetRow = useMemo(
		() =>
			targetHppdRows.reduce<Record<string, DaysInWeek[]>>((acc, row) => {
				acc[row.id] = targetHppdRows.filter((r) => r.id !== row.id).flatMap((r) => r.days);
				return acc;
			}, {}),
		[targetHppdRows],
	);

	const minHppdValidation = useMemo(
		() => ({
			validationCallback: (value: string) => Number(value) >= 0 && Number(value) <= formData.maxValidHPPDPercent,
			helperText: { invalid: t("department.config.general.validHPPD.minMax") },
			performValidation: "always" as const,
			showValidWhenValid: "never" as const,
		}),
		[t, formData.maxValidHPPDPercent],
	);
	const maxHppdValidation = useMemo(
		() => ({
			validationCallback: (value: string) => Number(value) >= 0 && Number(value) >= formData.minValidHPPDPercent,
			helperText: { invalid: t("department.config.general.validHPPD.minMax") },
			performValidation: "always" as const,
			showValidWhenValid: "never" as const,
		}),
		[t, formData.minValidHPPDPercent],
	);

	return (
		<Box component="div" sx={SIDE_LAID_GROUND_SX}>
			<SettingsSection
				headerPlacement="side"
				icon={<BadgeOutlined />}
				title={t("department.config.general.section.identity")}
				description={t("department.config.general.section.identity.description")}
				minColumnWidth={FIELD_MIN_COLUMN_WIDTH}
				contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
				divider
				dataTestId="dept-config-general-identity"
			>
				<ReadOnlyMetadataField
					label={t("department.config.general.id.label")}
					value={formData.id ?? buildDepartmentIdPreview(formData.displayName, formData.hospital)}
					copyable
					dataTestId="dept-config-general-id"
				/>
				<SystemValidationTextField
					label={t("department.config.general.displayName.label")}
					value={formData.displayName}
					onChange={(event) => setFormData((prev) => ({ ...prev, displayName: event.target.value }))}
					fullWidth
					dataTestId="dept-config-general-display-name"
				/>
				<SystemValidationTextField
					label={t("department.config.general.network.label")}
					value={formData.hospital}
					onChange={(event) => setFormData((prev) => ({ ...prev, hospital: event.target.value }))}
					fullWidth
					dataTestId="dept-config-general-network"
				/>
				<SystemDropdown
					fullWidth
					id={departmentTypeId}
					label={t("department.config.general.departmentType.label")}
					required
					errorKey={isNewDept && !formData.departmentType ? "department.config.general.departmentType.required" : undefined}
					items={Object.values(DepartmentType).map((deptType) => ({ value: deptType, label: deptType }))}
					selectedItem={formData.departmentType}
					onChange={(value) => {
						if (value) setFormData((prev) => ({ ...prev, departmentType: value }));
					}}
					data-testid="dept-config-general-department-type"
				/>
				<SystemAutoComplete
					freeSolo
					fullWidth
					disabled={isTimezoneLocked}
					options={TIMEZONES.map((tz) => ({ label: tz, value: tz }))}
					value={formData.timezone ? { label: formData.timezone, value: formData.timezone } : null}
					onChange={(_event, option) => setFormData((prev) => ({ ...prev, timezone: option?.value ?? "" }))}
					textFieldProps={{
						label: t("department.config.general.timezone.label"),
						required: true,
					}}
					dataTestId="dept-config-general-timezone"
				/>
				{isTimezoneLockUnknown && (
					<SystemBanner
						tone="warning"
						title={t("department.config.general.timezone.lockUnknown.title")}
						description={t("department.config.general.timezone.lockUnknown.description")}
						dataTestId="dept-config-general-timezone-lock-unknown"
						sx={{ gridColumn: "1 / -1" }}
					/>
				)}
			</SettingsSection>

			<SettingsSection
				headerPlacement="side"
				icon={<MonitorHeartOutlined />}
				title={t("department.config.general.section.staffingTargets")}
				description={t("department.config.general.section.staffingTargets.description")}
				minColumnWidth={FIELD_MIN_COLUMN_WIDTH}
				contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
				divider
				dataTestId="dept-config-general-staffing-targets"
			>
				<SystemDropdown
					fullWidth
					label={t("department.config.general.statsMode.label")}
					items={[
						{ value: StatsMode.PPD_HPPD, label: t("department.config.general.statsMode.ppdHppd") },
						{ value: StatsMode.Ratio, label: t("department.config.general.statsMode.ratio") },
					]}
					selectedItem={formData.statsMode}
					onChange={(value) => {
						if (value) setFormData((prev) => ({ ...prev, statsMode: value }));
					}}
					data-testid="dept-config-general-stats-mode"
				/>
				<SystemValidationTextField
					label={t("department.config.general.targetPPD.label")}
					inputType="number"
					value={String(formData.targetPPD)}
					onChange={(event) => setFormData((prev) => ({ ...prev, targetPPD: +event.target.value }))}
					fullWidth
					dataTestId="dept-config-general-target-ppd"
				/>
				<SystemValidationTextField
					label={t("department.config.general.numberOfBeds.label")}
					inputType="number"
					value={String(formData.totalBeds)}
					onChange={(event) => setFormData((prev) => ({ ...prev, totalBeds: +event.target.value }))}
					fullWidth
					dataTestId="dept-config-general-number-of-beds"
				/>
				<SystemValidationTextField
					label={t("department.config.general.minValidHPPD.label")}
					inputType="number"
					value={String(formData.minValidHPPDPercent)}
					onChange={(event) => setFormData((prev) => ({ ...prev, minValidHPPDPercent: +event.target.value }))}
					validation={minHppdValidation}
					inputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
					fullWidth
					dataTestId="dept-config-general-min-valid-hppd"
				/>
				<SystemValidationTextField
					label={t("department.config.general.maxValidHPPD.label")}
					inputType="number"
					value={String(formData.maxValidHPPDPercent)}
					onChange={(event) => setFormData((prev) => ({ ...prev, maxValidHPPDPercent: +event.target.value }))}
					validation={maxHppdValidation}
					inputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
					fullWidth
					dataTestId="dept-config-general-max-valid-hppd"
				/>
				<Box sx={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 1 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
						<Typography component="span" sx={{ fontWeight: 600, fontSize: "0.75rem", lineHeight: 1.2, color: INK.MUTED }}>
							{t("department.config.general.targetHPPD.label")}
						</Typography>
						<Typography component="span" sx={{ fontSize: "0.75rem", lineHeight: 1.4, color: INK.MUTED }}>
							{t("department.config.general.targetHPPD.helperText")}
						</Typography>
					</Box>
					<Box sx={SUBGROUP_BORDERED_SX}>
						{targetHppdRows.map((row, index) => {
							const isLastRow = index === targetHppdRows.length - 1;
							return (
								<Box key={row.id} sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
									<DaysInWeekSelector
										size="small"
										gap="2px"
										selectedDays={row.days}
										disabledDays={disabledDaysPerTargetRow[row.id]}
										onChange={(selected) => handleUpdateTargetHppdRow(row.id, { days: selected })}
									/>
									<SystemValidationTextField
										label={t("department.config.general.targetHPPD.value.label")}
										inputType="number"
										value={row.target.toString()}
										onChange={(event) => handleUpdateTargetHppdRow(row.id, { target: Number.parseFloat(event.target.value) || 0 })}
										inputProps={{ inputProps: { min: 0, step: 0.01 } }}
										inputSx={{ width: "100px" }}
										reserveHeightForHelperText={false}
									/>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
										<SystemButton
											buttonVariant="borderless"
											size="small"
											leadingIcon={<DeleteIcon fontSize="small" />}
											onClick={() => handleRemoveTargetHppdRow(row.id)}
											disabled={targetHppdRows.length <= 1}
											tooltip={t("department.config.general.targetHPPD.remove")}
											aria-label={t("department.config.general.targetHPPD.remove")}
											data-testid="dept-config-general-target-hppd-remove"
										/>
										{isLastRow && (
											<SystemButton
												buttonVariant="borderless"
												size="small"
												leadingIcon={<AddIcon fontSize="small" />}
												onClick={handleAddTargetHppdRow}
												tooltip={t("department.config.general.targetHPPD.add")}
												aria-label={t("department.config.general.targetHPPD.add")}
												data-testid="dept-config-general-target-hppd-add"
											/>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>
			</SettingsSection>

			<SettingsSection
				headerPlacement="side"
				icon={<CalendarMonthOutlined />}
				title={t("department.config.general.section.schedule")}
				description={t("department.config.general.section.schedule.description")}
				minColumnWidth={FIELD_MIN_COLUMN_WIDTH}
				contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
				divider
				dataTestId="dept-config-general-schedule"
			>
				<DateInput
					label={t("department.config.general.firstSunday.label")}
					value={formData.week1FirstSunday}
					onChange={(newDate) => {
						setFormData((prev) => ({ ...prev, ...(!!newDate && { week1FirstSunday: newDate }) }));
					}}
					shouldDisableDate={(date) => getWeekdayName(date.weekday % 7) !== "Sunday"}
					formControlSx={{ width: "100%" }}
					dataTestId="dept-config-general-first-sunday"
				/>
				<SystemAutoComplete
					freeSolo
					fullWidth
					options={[]}
					value={formData.dayStart != null ? { label: floatToTime(formData.dayStart), value: floatToTime(formData.dayStart) } : null}
					onInputChange={(_event, value, reason) => {
						if (reason === "reset") return;
						validateTimeInput(value, (dayStartFloat) => {
							setFormData((prev) => ({ ...prev, dayStart: dayStartFloat }));
						});
					}}
					textFieldProps={{
						label: t("department.config.general.dayStart.label"),
						defaultHelperText: t("department.config.general.dayStart.helperText"),
						labelTooltip: t("department.config.general.dayStart.tooltip"),
					}}
					dataTestId="dept-config-general-day-start"
				/>
				<SystemDropdown<number>
					fullWidth
					id={masterScheduleWeeksId}
					label={t("department.config.general.scheduleTemplateWeeks.label")}
					items={[
						{ value: 1, label: "1 week" },
						{ value: 2, label: "2 weeks" },
					]}
					selectedItem={formData.masterScheduleWeeks}
					onChange={(value) => {
						if (value != null) setFormData((prev) => ({ ...prev, masterScheduleWeeks: value }));
					}}
					data-testid="dept-config-general-schedule-template-weeks"
				/>
				<SystemValidationTextField
					label={t("department.config.general.autoCreateScheduleWeeksAhead.label")}
					inputType="number"
					value={String(formData.autoCreateScheduleWeeksAhead)}
					onChange={(event) => setFormData((prev) => ({ ...prev, autoCreateScheduleWeeksAhead: +event.target.value }))}
					validation={{
						validationCallback: (value: string) => Number.isInteger(Number(value)) && Number(value) >= 1,
						helperText: { invalid: t("department.config.general.weeksHorizon.invalid") },
						performValidation: "always" as const,
						showValidWhenValid: "never" as const,
					}}
					inputProps={{ inputProps: { min: 1, step: 1 } }}
					defaultHelperText={t("department.config.general.autoCreateScheduleWeeksAhead.helperText")}
					fullWidth
					dataTestId="dept-config-general-auto-create-schedule-weeks-ahead"
				/>
				<SystemValidationTextField
					label={t("department.config.general.dataHistoryWeeks.label")}
					inputType="number"
					value={String(formData.departmentDataHistoryWeeks)}
					onChange={(event) => setFormData((prev) => ({ ...prev, departmentDataHistoryWeeks: +event.target.value }))}
					validation={{
						validationCallback: (value: string) => Number.isInteger(Number(value)) && Number(value) >= 1,
						helperText: { invalid: t("department.config.general.weeksHorizon.invalid") },
						performValidation: "always" as const,
						showValidWhenValid: "never" as const,
					}}
					inputProps={{ inputProps: { min: 1, step: 1 } }}
					defaultHelperText={t("department.config.general.dataHistoryWeeks.helperText")}
					fullWidth
					dataTestId="dept-config-general-data-history-weeks"
				/>
				<SystemValidationTextField
					label={t("department.config.general.cycleDuration.label")}
					inputType="number"
					value={formData.prnCycleDurationDays ? String(formData.prnCycleDurationDays) : ""}
					onChange={(event) => setFormData((prev) => ({ ...prev, prnCycleDurationDays: +event.target.value }))}
					inputProps={{ endAdornment: <InputAdornment position="end">{t("department.config.general.cycleDuration.unit")}</InputAdornment> }}
					fullWidth
					dataTestId="dept-config-general-cycle-duration"
				/>
				<SystemValidationTextField
					label={t("settings.nurse.inactivity.alert.days")}
					inputType="number"
					value={formData.nurseInactivityAlertDays != null ? String(formData.nurseInactivityAlertDays) : ""}
					onChange={(event) => setFormData((prev) => ({ ...prev, nurseInactivityAlertDays: event.target.value ? +event.target.value : null }))}
					validation={{
						validationCallback: (value: string) => Number(value) >= 1,
						helperText: { invalid: t("settings.nurse.inactivity.alert.days.error") },
						performValidation: "always" as const,
						showValidWhenValid: "never" as const,
					}}
					inputProps={{ inputProps: { min: 1 } }}
					defaultHelperText={t("settings.nurse.inactivity.alert.days.helper", { inactiveDays: formData.nurseInactiveAfterDays ?? 90 })}
					fullWidth
					dataTestId="dept-config-general-nurse-inactivity-alert-days"
				/>
				<SystemValidationTextField
					label={t("settings.nurse.inactive.after.days")}
					inputType="number"
					value={formData.nurseInactiveAfterDays != null ? String(formData.nurseInactiveAfterDays) : ""}
					onChange={(event) => setFormData((prev) => ({ ...prev, nurseInactiveAfterDays: event.target.value ? +event.target.value : null }))}
					validation={{
						validationCallback: (value: string) => {
							const n = Number(value);
							if (n < 2) return false;
							return n > (formData.nurseInactivityAlertDays ?? 0);
						},
						helperText: {
							invalid:
								Number(formData.nurseInactiveAfterDays) < 2
									? t("settings.nurse.inactive.after.days.minimum.error")
									: t("settings.nurse.inactive.after.days.error"),
						},
						performValidation: "always" as const,
						showValidWhenValid: "never" as const,
					}}
					inputProps={{ inputProps: { min: 2 } }}
					defaultHelperText={t("settings.nurse.inactive.after.days.helper", {
						alertDays: formData.nurseInactivityAlertDays ?? DEFAULT_NURSE_INACTIVITY_ALERT_DAYS,
					})}
					fullWidth
					dataTestId="dept-config-general-nurse-inactive-after-days"
				/>
			</SettingsSection>

			<SettingsSection
				headerPlacement="side"
				icon={<GroupsOutlined />}
				title={t("department.config.general.section.roles")}
				description={t("department.config.general.section.roles.description")}
				layout="stack"
				contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
				divider
				dataTestId="dept-config-general-roles"
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "stretch" }}>
					{/* Roles holds 10+ chips → full width so none truncate; Default Role is one value → narrow, below. */}
					<Box sx={{ width: "100%" }}>
						<SystemDropdown
							selectSx={{ minHeight: "58px" }}
							id="roles-selector"
							label={t("department.config.general.roles.label")}
							multiple
							fullWidth
							items={Object.entries(NurseFullRole).map(([key, value]) => ({
								value: key,
								label: value,
								selectedLabel: nurseRoleDisplayName[key as NurseRole] ?? key,
								color: roleColor(value).dot,
							}))}
							selectedItems={formData.roles}
							onChangeMultiple={(roles) => {
								const nurseRoles = roles.filter((role): role is NurseRole => {
									return Object.keys(NurseRole).includes(role);
								});
								setFormData((prev) => {
									const updatedPartialBlockShifts = nurseRoles.reduce<Department["partial_block_shifts"]>((accu, current) => {
										accu[current] = prev.partialBlockShifts[current] || [...PARTIAL_SHIFT_BLOCK_HOURS];
										return accu;
									}, {});
									updatedPartialBlockShifts.default = prev.partialBlockShifts.default || [];

									const nextNight = nurseRoles.reduce<Partial<Record<NurseRole, number>>>((acc, role) => {
										acc[role] = prev.departmentWageConfig.nightRatePayValue?.[role] ?? DEFAULT_NIGHT_PAY_VALUE[role] ?? 0;
										return acc;
									}, {});
									const nextWeekend = nurseRoles.reduce<Partial<Record<NurseRole, number>>>((acc, role) => {
										acc[role] = prev.departmentWageConfig.weekendRatePayValue?.[role] ?? DEFAULT_WEEKEND_PAY_VALUE[role] ?? 0;
										return acc;
									}, {});

									return {
										...prev,
										roles: nurseRoles,
										defaultRole: prev.defaultRole && nurseRoles.includes(prev.defaultRole) ? prev.defaultRole : undefined,
										partialBlockShifts: updatedPartialBlockShifts,
										departmentWageConfig: {
											...prev.departmentWageConfig,
											nightRatePayValue: nextNight,
											weekendRatePayValue: nextWeekend,
											holidayRatePayPercentage: prev.departmentWageConfig.holidayRatePayPercentage,
											overtimeRatePayPercentage: prev.departmentWageConfig.overtimeRatePayPercentage,
											trainingWageType: prev.departmentWageConfig.trainingWageType,
											trainingWageValue: prev.departmentWageConfig.trainingWageValue,
											orientationWageType: prev.departmentWageConfig.orientationWageType,
											orientationWageValue: prev.departmentWageConfig.orientationWageValue,
											overtimeDailyHoursLimit: prev.departmentWageConfig.overtimeDailyHoursLimit,
											overtimeWeeklyHoursLimit: prev.departmentWageConfig.overtimeWeeklyHoursLimit,
										},
										specialNurses: prev.specialNurses,
									};
								});
							}}
						/>
					</Box>
					<Box sx={{ width: { xs: "100%", sm: 320 }, flexShrink: 0 }}>
						<SystemDropdown
							fullWidth
							id="default-role-selector"
							label={t("department.config.general.defaultRole.label")}
							clearable
							items={formData.roles.map((roleKey) => ({
								value: roleKey,
								label: NurseFullRole[roleKey],
								selectedLabel: nurseRoleDisplayName[roleKey] ?? roleKey,
								color: roleColor(NurseFullRole[roleKey]).dot,
							}))}
							selectedItem={formData.defaultRole}
							onChange={(role) =>
								setFormData((prev) => ({
									...prev,
									defaultRole: (role as NurseRole | undefined) ?? undefined,
								}))
							}
							disabled={formData.roles.length === 0}
							data-testid="dept-config-general-default-role"
						/>
					</Box>
				</Box>
			</SettingsSection>

			<SettingsSection
				headerPlacement="side"
				icon={<GavelOutlined />}
				title={t("department.config.general.section.rules")}
				description={t("department.config.general.section.rules.description")}
				minColumnWidth={FIELD_MIN_COLUMN_WIDTH}
				contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
				dataTestId="dept-config-general-rules"
			>
				<SystemDropdown
					fullWidth
					label={t("department.config.general.removeUnionStaffWarning.label")}
					selectedItem={formData.removeUnionStaffWarning}
					onChange={(selectedValue) => {
						if (!selectedValue) return;
						setFormData((prev) => ({ ...prev, removeUnionStaffWarning: selectedValue }));
					}}
					items={[
						{ value: RemoveUnionStaffWarning.None, label: t("department.config.general.removeUnionStaffWarning.never") },
						{ value: RemoveUnionStaffWarning.OnlyRecurring, label: t("department.config.general.removeUnionStaffWarning.onlyRecurring") },
						{ value: RemoveUnionStaffWarning.All, label: t("department.config.general.removeUnionStaffWarning.all") },
					]}
					data-testid="dept-config-general-remove-union-warning"
				/>
				<SystemDropdown
					fullWidth
					label={t("department.config.general.shiftDeltaThreshold.label")}
					items={shiftDeltaThresholdOptions}
					selectedItem={formData.shiftDeltaThresholdHours}
					onChange={(value) => {
						if (value != null) setFormData((prev) => ({ ...prev, shiftDeltaThresholdHours: value }));
					}}
					data-testid="dept-config-general-shift-delta-threshold"
				/>
			</SettingsSection>
		</Box>
	);
};
